import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { EmailAddress } from '../types'
import { apiClient } from '../services/api'
import { useApiAuth } from '../hooks/useApiAuth'
import Header from '../components/Header'
import {
  EXTENSION_STORE_URL,
  installedExtensionVersion,
  isChromiumDesktop,
} from '../utils/extensionPrompt'

export default function SettingsPage() {
  const { ready: authReady } = useApiAuth()
  const navigate = useNavigate()
  const { logout } = useAuth0()

  // Read once on mount: the content script stamps <html> before our bundle runs,
  // so this never changes for the life of the page.
  const [extensionVersion] = useState(installedExtensionVersion)
  const [canInstallExtension] = useState(isChromiumDesktop)

  const [addresses, setAddresses] = useState<EmailAddress[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [newEmail, setNewEmail] = useState('')
  const [addingError, setAddingError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const [resendingFor, setResendingFor] = useState<string | null>(null)
  const [resendSuccess, setResendSuccess] = useState<string | null>(null)
  const [resendError, setResendError] = useState<string | null>(null)

  // Inline delete confirmation: holds the email address awaiting confirmation.
  const [confirmDeleteFor, setConfirmDeleteFor] = useState<string | null>(null)
  const [deletingFor, setDeletingFor] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  useEffect(() => {
    if (!authReady) return
    apiClient
      .getEmailAddresses()
      .then(setAddresses)
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Failed to load email addresses')
      )
      .finally(() => setLoading(false))
  }, [authReady])

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault()
    const email = newEmail.trim().toLowerCase()
    if (!email) return
    setAdding(true)
    setAddingError(null)
    try {
      const added = await apiClient.addEmailAddress(email)
      setAddresses((prev) => [...prev, added])
      setNewEmail('')
    } catch (err: unknown) {
      setAddingError(err instanceof Error ? err.message : 'Failed to add address')
    } finally {
      setAdding(false)
    }
  }

  async function handleDelete(email: string) {
    setDeletingFor(email)
    setDeleteError(null)
    try {
      await apiClient.deleteEmailAddress(email)
      setAddresses((prev) => prev.filter((a) => a.email !== email))
      setConfirmDeleteFor(null)
    } catch (err: unknown) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to remove address')
    } finally {
      setDeletingFor(null)
    }
  }

  async function handleResend(email: string) {
    setResendingFor(email)
    setResendSuccess(null)
    setResendError(null)
    try {
      await apiClient.resendVerification(email)
      setResendSuccess(email)
      // Auto-clear the success message so it doesn't linger when the user
      // resends to a different address later.
      setTimeout(() => setResendSuccess((cur) => (cur === email ? null : cur)), 3000)
    } catch (err: unknown) {
      setResendError(err instanceof Error ? err.message : 'Failed to resend verification email')
    } finally {
      setResendingFor(null)
    }
  }

  const atLimit = addresses.length >= 3

  return (
    <>
      <Header />
      <main className="page">
        <button className="back-link" onClick={() => navigate(-1)}>
          ← Back
        </button>

        <h2 className="settings-heading">Settings</h2>

        <section className="settings-section">
          <h3 className="settings-section-heading">Email addresses</h3>
          <p className="settings-description">
            Add up to 3 email addresses. Email <strong>save@eizel.co</strong> from any verified
            address to save content directly to your library.
          </p>

          {loading ? (
            <div className="spinner settings-spinner" />
          ) : error ? (
            <div className="empty-state">
              <p>{error}</p>
            </div>
          ) : (
            <>
              {addresses.length > 0 && (
                <ul className="email-address-list">
                  {addresses.map((addr) => (
                    <li key={addr.email} className="email-address-item">
                      <div className="email-address-info">
                        <span className="email-address-value">{addr.email}</span>
                        <span className={`email-address-status ${addr.status}`}>
                          {addr.status === 'verified' ? 'Verified' : 'Pending verification'}
                        </span>
                      </div>
                      <div className="email-address-actions">
                        {addr.status === 'pending' && (
                          <button
                            className="btn-ghost btn-ghost--sm"
                            onClick={() => handleResend(addr.email)}
                            disabled={resendingFor === addr.email}
                          >
                            {resendingFor === addr.email
                              ? 'Sending...'
                              : resendSuccess === addr.email
                                ? 'Sent!'
                                : 'Resend verification'}
                          </button>
                        )}
                        {confirmDeleteFor === addr.email ? (
                          <>
                            <button
                              className="btn-danger btn-danger--sm"
                              onClick={() => handleDelete(addr.email)}
                              disabled={deletingFor === addr.email}
                            >
                              {deletingFor === addr.email ? 'Removing…' : 'Confirm'}
                            </button>
                            <button
                              className="btn-ghost btn-ghost--sm"
                              onClick={() => {
                                setConfirmDeleteFor(null)
                                setDeleteError(null)
                              }}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            className="btn-danger btn-danger--sm"
                            onClick={() => setConfirmDeleteFor(addr.email)}
                            title="Remove address"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {resendError && <p className="settings-inline-error">{resendError}</p>}
              {deleteError && <p className="settings-inline-error">{deleteError}</p>}

              {!atLimit && (
                <form className="email-address-form" onSubmit={handleAdd}>
                  <input
                    type="email"
                    className="save-input"
                    placeholder="you@example.com"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    disabled={adding}
                  />
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={adding || !newEmail.trim()}
                  >
                    {adding ? 'Adding…' : 'Add'}
                  </button>
                </form>
              )}

              {atLimit && (
                <p className="settings-description settings-limit-note">
                  You've reached the 3-address limit.
                </p>
              )}

              {addingError && (
                <p className="save-error" style={{ marginTop: 8 }}>
                  {addingError}
                </p>
              )}
            </>
          )}
        </section>

        <section className="settings-section">
          <h3 className="settings-section-heading">Browser extension</h3>
          {extensionVersion ? (
            <p className="settings-description">
              Eizel Saver {extensionVersion} is installed. Click its toolbar icon on any page to
              save it.
            </p>
          ) : (
            <>
              <p className="settings-description">
                Save the page you are reading in one click, without copying its URL. Available for
                Chrome, Edge, Brave, and other Chromium browsers.
              </p>
              {/* Same gate as the banner. A Chrome Web Store link is a dead end
                  in Safari, Firefox, and on mobile, so say so instead. */}
              {canInstallExtension ? (
                <a
                  className="btn-secondary"
                  href={EXTENSION_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get the extension
                </a>
              ) : (
                <p className="settings-description settings-limit-note">
                  Open this page in a Chromium browser on desktop to install it.
                </p>
              )}
            </>
          )}
        </section>

        <section className="settings-section--logout">
          <button
            className="btn-secondary"
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.8}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"
              />
            </svg>
            Sign out on this device
          </button>
        </section>
      </main>
    </>
  )
}
